import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Download, Loader2 } from 'lucide-react';

// Contexts
import { useEvents } from '@/contexts/EventsContext';
import { useClients } from '@/contexts/ClientsContext';
import { useAnalytics } from '@/contexts/AnalyticsContext';
import { useUsers } from '@/contexts/UsersContext';
import { useAuth } from '@/contexts/AuthContext';

// Components
import AnalyticsDateSelector from '@/components/analytics/AnalyticsDateSelector';
import AnalyticsKPIs from '@/components/analytics/AnalyticsKPIs';
import AnalyticsCharts from '@/components/analytics/AnalyticsCharts';
import AnalyticsStaffProducts from '@/components/analytics/AnalyticsStaffProducts';
import AnalyticsTopClients from '@/components/analytics/AnalyticsTopClients'; // <--- NUOVO IMPORT
import AnalyticsBookingFlow from '@/components/analytics/AnalyticsBookingFlow';
import AnalyticsListModal from '@/components/modals/AnalyticsListModal';

import { useTranslation } from 'react-i18next';
import { exportAnalyticsToExcel } from '@/lib/exportUtils';

const Analytics: React.FC = () => {
    const { t } = useTranslation();
    const [timeRange, setTimeRange] = useState('week');
    const [isExporting, setIsExporting] = useState(false);

    // Stato per il Modale Lista
    const [listModalType, setListModalType] = useState<'staff' | 'services' | null>(null);

    const { bookings, performances } = useEvents();
    const { clients } = useClients();
    const { users } = useUsers();
    const { isAdmin, user } = useAuth();

    const {
        filterDataByPeriod,
        getPreviousPeriodData,
        calculateHourlyDistribution,
        calculateWeeklyDistribution,
        calculateMonthlyDistribution,
        getValidBookings,
        buildTopServices,
        calculateDailyDistribution
    } = useAnalytics();

    // --- DATA ENRICHMENT ---
    const enrichedBookings = useMemo(() => {
        if (!bookings) return [];

        // Filtra le prenotazioni in base all'utente loggato
        let filteredBookings = bookings;
        if (!isAdmin && user) {
            filteredBookings = filteredBookings.filter((b: any) => b.hairdresser_id === user.id);
        }

        return filteredBookings.map((b: any) => {
            const perf = performances?.find((p: any) => p.id === b.performance_id);
            const basePrice = Number(perf?.price) || 0;
            const adjustment = Number(b.price_adjustment) || 0;
            const price = basePrice + adjustment;
            const serviceName = perf?.name || b.title || t('analytics.other');

            return {
                ...b,
                price: price,
                total_price: price,
                service_name: serviceName
            };
        });
    }, [bookings, performances, t, isAdmin, user]);

    // --- METRICHE ---
    const validBookings = useMemo(() => getValidBookings(enrichedBookings), [enrichedBookings, getValidBookings]);
    const validCurrentBookings = useMemo(() => filterDataByPeriod(validBookings, timeRange, 'bookings'), [validBookings, timeRange, filterDataByPeriod]);
    const currentClients = useMemo(() => filterDataByPeriod(clients, timeRange, 'clients'), [clients, timeRange, filterDataByPeriod]);

    const previousBookings = useMemo(() => getPreviousPeriodData(validBookings, timeRange, 'bookings'), [validBookings, timeRange, getPreviousPeriodData]);
    const previousClients = useMemo(() => getPreviousPeriodData(clients, timeRange, 'clients'), [clients, timeRange, getPreviousPeriodData]);

    const revenue = useMemo(() => validCurrentBookings.reduce((acc: number, b: any) => acc + (b.total_price || 0), 0), [validCurrentBookings]);
    const previousRevenue = useMemo(() => previousBookings.reduce((acc: number, b: any) => acc + (b.total_price || 0), 0), [previousBookings]);

    const newBookingsCount = validCurrentBookings.length;
    const previousBookingsCount = previousBookings.length;
    const newClientsCount = currentClients.length;
    const previousClientsCount = previousClients.length;

    const averageTicket = useMemo(() => (newBookingsCount > 0 ? revenue / newBookingsCount : 0), [revenue, newBookingsCount]);
    const prevAverageTicket = useMemo(() => (previousBookingsCount > 0 ? previousRevenue / previousBookingsCount : 0), [previousRevenue, previousBookingsCount]);

    const calculateVariation = (current: number, previous: number) => {
        if (!previous || previous === 0) return 0;
        return ((current - previous) / previous) * 100;
    };

    // --- CHART DATA ---
    const revenueChartData = useMemo(() => {
        if (timeRange === 'day') return calculateHourlyDistribution(validCurrentBookings, timeRange, 'revenue');
        if (timeRange === 'week') return calculateWeeklyDistribution(validCurrentBookings, timeRange, 'revenue');
        if (timeRange === 'month') return calculateDailyDistribution(validCurrentBookings, timeRange, 'revenue');
        return calculateMonthlyDistribution(validCurrentBookings, timeRange, 'revenue');
    }, [validCurrentBookings, timeRange, calculateHourlyDistribution, calculateWeeklyDistribution, calculateMonthlyDistribution, calculateDailyDistribution]);

    const servicesData = useMemo(() => {
        // Usa la funzione dal context
        const rawServices = buildTopServices(validCurrentBookings);

        // Calcola il totale per le percentuali
        const total = rawServices.reduce((acc: number, curr: any) => acc + curr.value, 0) || 1;
        const colors = ['#D96C4F', '#C05A40', '#F2B6A3', '#1A1A1A', '#4B5563', '#9CA3AF'];

        return rawServices.map((service: any, index: number) => ({
            name: service.name,
            value: Math.round((service.value / total) * 100),
            rawValue: service.value,
            color: colors[index % colors.length]
        }));
    }, [validCurrentBookings, buildTopServices]);

    const staffData = useMemo(() => {
        if (!users) return [];
        const staffMap = users.reduce((acc: any, user: any) => { acc[user.id] = user.first_name; return acc; }, {});
        const revenueByStaff: Record<string, number> = {};

        validCurrentBookings.forEach((b: any) => {
            const staffName = staffMap[b.hairdresser_id] || t('common.unknown');
            revenueByStaff[staffName] = (revenueByStaff[staffName] || 0) + (Number(b.total_price) || 0);
        });

        return Object.entries(revenueByStaff)
            .map(([name, revenue]) => ({ name, revenue }))
            .sort((a, b) => b.revenue - a.revenue);
    }, [validCurrentBookings, users, t]);

    // --- NUOVO CALCOLO: TOP CLIENTS ---
    const topClientsData = useMemo(() => {
        const counts: Record<string, number> = {};
        validCurrentBookings.forEach((b: any) => {
            if (b.client_id) {
                counts[b.client_id] = (counts[b.client_id] || 0) + 1;
            }
        });

        return Object.entries(counts)
            .map(([id, count]) => {
                const client = clients.find((c: any) => c.id === id);
                return {
                    id,
                    name: client ? client.full_name || `${client.first_name || ''} ${client.last_name || ''}`.trim() : t('common.unknown'),
                    count
                };
            })
            .sort((a, b) => b.count - a.count)
            .slice(0, 5); // Top 5
    }, [validCurrentBookings, clients, t]);


    const handleExportClick = async () => {
        setIsExporting(true);
        setTimeout(() => {
            exportAnalyticsToExcel({
                timeRange, revenue, previousRevenue, newBookingsCount, previousBookingsCount,
                averageTicket, prevAverageTicket, newClientsCount, previousClientsCount,
                revenueChartData, staffData, servicesData, calculateVariation
            });
            setIsExporting(false);
        }, 100);
    };

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
    };

    if (!bookings || !clients) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
            </div>
        );
    }

    return (
        <>
            <motion.div
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                className="max-w-7xl mx-auto space-y-8"
            >
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900">{t('analytics.title')}</h1>
                        <p className="text-gray-500 mt-1">{t('analytics.subtitle')}</p>
                    </div>

                    <div className="flex items-center gap-3">
                        <AnalyticsDateSelector value={timeRange} onChange={setTimeRange} />
                        <button
                            onClick={handleExportClick}
                            disabled={isExporting}
                            className="flex items-center gap-2 px-4 py-2.5 bg-gray-900 text-white rounded-xl font-bold shadow-lg hover:bg-black transition-colors disabled:opacity-70 active:scale-95"
                        >
                            {isExporting ? <Loader2 size={18} className="animate-spin" /> : <Download size={18} />}
                            <span className="hidden sm:inline">{isExporting ? t('analytics.exporting') : t('analytics.export_excel')}</span>
                        </button>
                    </div>
                </div>

                <AnalyticsKPIs
                    revenue={revenue}
                    revenueVariation={calculateVariation(revenue, previousRevenue)}
                    bookings={newBookingsCount}
                    bookingsVariation={calculateVariation(newBookingsCount, previousBookingsCount)}
                    ticket={averageTicket}
                    ticketVariation={calculateVariation(averageTicket, prevAverageTicket)}
                    clients={newClientsCount}
                    clientsVariation={calculateVariation(newClientsCount, previousClientsCount)}
                />

                <AnalyticsCharts
                    revenueData={revenueChartData}
                    servicesData={servicesData}
                    totalAppointments={newBookingsCount}
                />

                <AnalyticsStaffProducts
                    staffData={staffData}
                    servicesData={servicesData}
                    onOpenListModal={(type) => setListModalType(type)}
                />

                {/* NUOVA SEZIONE: Top Clients + Flusso di prenotazione */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <AnalyticsTopClients data={topClientsData} />
                    <AnalyticsBookingFlow bookings={validCurrentBookings} />
                </div>

            </motion.div>

            {/* Modale Liste */}
            <AnalyticsListModal
                isOpen={!!listModalType}
                onClose={() => setListModalType(null)}
                title={listModalType === 'staff' ? 'Performance Staff' : 'Tutti i Servizi'}
                type={listModalType || 'staff'}
                data={listModalType === 'staff' ? staffData : servicesData}
            />
        </>
    );
};

export default Analytics;